import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Key, Plus, Search, Eye, EyeOff, Copy, Check, Lock, Building2, Trash2 } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function PasswordVaultModule() {
  const [passwords, setPasswords] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: '',
    title: '',
    username: '',
    password_encrypted: '',
    url: '',
    category: 'application',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [pwdRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/passwords`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (pwdRes.ok) setPasswords(await pwdRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/passwords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowAddDialog(false);
        setFormData({
          organization_id: '',
          title: '',
          username: '',
          password_encrypted: '',
          url: '',
          category: 'application',
          notes: ''
        });
        loadData();
      }
    } catch (err) {
      console.error('Failed to create password:', err);
      alert('Failed to create password');
    }
  };

  const handleReveal = async (pwdId) => {
    if (revealedPasswords[pwdId]) {
      // Hide password
      setRevealedPasswords(prev => ({ ...prev, [pwdId]: null }));
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/passwords/${pwdId}/reveal`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRevealedPasswords(prev => ({ ...prev, [pwdId]: data.password }));
      }
    } catch (err) {
      console.error('Failed to reveal password:', err);
      alert('Failed to reveal password');
    }
  };

  const handleCopy = (text, pwdId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(pwdId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (pwdId, title) => {
    if (!confirm(`Delete password: ${title}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/passwords/${pwdId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete password:', err);
    }
  };

  const filteredPasswords = passwords.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categoryColors = {
    'email': 'bg-blue-100 text-blue-700',
    'server': 'bg-green-100 text-green-700',
    'application': 'bg-purple-100 text-purple-700',
    'network': 'bg-orange-100 text-orange-700',
    'database': 'bg-red-100 text-red-700',
    'other': 'bg-slate-100 text-slate-700'
  };

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Lock className="w-8 h-8 text-purple-600" />
              Password Vault
            </h1>
            <p className="text-sm text-slate-500 mt-1">Secure credential storage with AES-256 encryption</p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Password
            </Button>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Password</DialogTitle>
                <DialogDescription>Store credentials securely with AES-256 encryption</DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Organization *</Label>
                  <Select value={formData.organization_id} onValueChange={(value) => setFormData({...formData, organization_id: value})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Title *</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Office 365 Admin Account" required />
                  </div>
                  <div>
                    <Label>Username/Email</Label>
                    <Input value={formData.username} onChange={(e) => setFormData({...formData, username: e.target.value})} placeholder="admin@company.com" />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={formData.password_encrypted} onChange={(e) => setFormData({...formData, password_encrypted: e.target.value})} required />
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="https://portal.office.com" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="server">Server</SelectItem>
                        <SelectItem value="application">Application</SelectItem>
                        <SelectItem value="network">Network</SelectItem>
                        <SelectItem value="database">Database</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    <Lock className="w-4 h-4 mr-2" />
                    Encrypt & Save
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input placeholder="Search passwords..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12"><p className="text-slate-500">Loading passwords...</p></div>
      ) : filteredPasswords.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Lock className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 font-medium">No passwords stored</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPasswords.map((pwd) => (
            <Card key={pwd.id} className="hover:shadow-md transition-shadow border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Key className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-slate-800">{pwd.title}</h3>
                      <Badge className={categoryColors[pwd.category] || categoryColors.other}>
                        {pwd.category}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Organization</p>
                        <div className="flex items-center gap-1 font-medium">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {pwd.organization_name}
                        </div>
                      </div>
                      {pwd.username && (
                        <div>
                          <p className="text-slate-500">Username</p>
                          <p className="font-medium flex items-center gap-2">
                            {pwd.username}
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleCopy(pwd.username, `${pwd.id}-user`)}>
                              {copiedId === `${pwd.id}-user` ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                            </Button>
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-500">Password</p>
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                            {revealedPasswords[pwd.id] || '••••••••••'}
                          </code>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleReveal(pwd.id)}>
                            {revealedPasswords[pwd.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </Button>
                          {revealedPasswords[pwd.id] && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleCopy(revealedPasswords[pwd.id], pwd.id)}>
                              {copiedId === pwd.id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {pwd.url && (
                      <div className="mt-2">
                        <a href={pwd.url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 hover:underline">
                          {pwd.url}
                        </a>
                      </div>
                    )}
                    
                    {pwd.last_accessed_at && (
                      <p className="text-xs text-slate-400 mt-2">
                        Last accessed: {new Date(pwd.last_accessed_at).toLocaleString()} by {pwd.last_accessed_by}
                      </p>
                    )}
                  </div>
                  
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(pwd.id, pwd.title)}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
